import { useMemo } from 'react';
import { useBrandMenus, type BrandMenus, type SidebarMenuItem, type TopNavMenuItem } from './menus';
import { useBrandFeatures } from './features';

/**
 * Which URL each Menu Visibility entry actually links to.
 *
 * `menus.ts` only says whether a link is *painted*. This file says where that
 * link *goes*, which is what lets the router refuse a page the super admin just
 * switched off — otherwise a hidden entry only ever removed the button and the
 * page still answered to anyone typing the URL.
 *
 * Several entries share one URL: "Casino" in the top nav and "Our Casino" in the
 * sidebar are the same lobby, and `/casino/all/live` answers to three rows at
 * once (Live Casino, Our VIP Casino and the nav's Live Casino). A toggle names a
 * PAGE, so those move as one — see `isRouteHidden`.
 *
 * The paths MUST stay in step with the components that draw them:
 *   - `topNav`  → `components/Navbar.tsx` and `components/MobileNavbar.tsx`
 *   - `sidebar` → the `othersTree` / `referEarn` rows in `common/Sidebar.tsx`
 *
 * Only entries that navigate somewhere are listed. The sidebar's Racing and All
 * Sports rows expand a tree in place rather than routing, and the home page's
 * sport chips only re-filter the odds table, so none of them owns a URL and
 * hiding them can't take one away.
 *
 * The rail's section headers (`secOthers` and friends) are absent for a
 * different reason: a section is a *layout* switch — "collapse this block out of
 * the sidebar" — not a verdict on the pages inside it. `Sidebar.tsx` drops the
 * block when its header is off, and a lobby the top nav still links to stays
 * open. Listing them here would make collapsing the rail close the nav's Casino.
 */

type MenuLink =
  | { group: 'topNav'; id: TopNavMenuItem; path: string }
  | { group: 'sidebar'; id: SidebarMenuItem; path: string };

export const MENU_LINKS: readonly MenuLink[] = [
  // --- Top navigation strip -------------------------------------------------
  { group: 'topNav', id: 'home', path: '/home' },
  { group: 'topNav', id: 'casino', path: '/casino/all' },
  { group: 'topNav', id: 'slots', path: '/casino/all/slots' },
  { group: 'topNav', id: 'liveCasino', path: '/casino/all/live' },
  { group: 'topNav', id: 'tableGames', path: '/casino/all/table' },
  { group: 'topNav', id: 'crash', path: '/casino/all/crash' },
  { group: 'topNav', id: 'lottery', path: '/casino/all/lottery' },
  { group: 'topNav', id: 'inplay', path: '/inplay' },
  { group: 'topNav', id: 'cricket', path: '/all-sports/Cricket' },
  { group: 'topNav', id: 'tennis', path: '/all-sports/Tennis' },
  { group: 'topNav', id: 'football', path: '/all-sports/Soccer' },
  { group: 'topNav', id: 'tableTennis', path: '/all-sports/Table-tennis' },

  // --- Left sidebar ---------------------------------------------------------
  { group: 'sidebar', id: 'referEarn', path: '/user/affiliate' },
  { group: 'sidebar', id: 'casino', path: '/casino/all' },
  { group: 'sidebar', id: 'vip', path: '/casino/all/live' },
  { group: 'sidebar', id: 'premium', path: '/casino/all/table' },
  { group: 'sidebar', id: 'virtual', path: '/casino/all/virtual' },
  { group: 'sidebar', id: 'live', path: '/casino/all/live' },
  { group: 'sidebar', id: 'slot', path: '/casino/all/slots' },
  { group: 'sidebar', id: 'fantasy', path: '/casino/all/fishing' },
];

/**
 * Collapse a pathname to the thing a menu entry can own.
 *
 * `/casino/:provider/:category` is one route serving six different nav items —
 * the provider segment is a catalog filter the player picks, not something the
 * panel can hide, so it is dropped and the category alone identifies the page.
 * That way `/casino/JILI/slots` is judged by the same "Slots" toggle as the
 * `/casino/all/slots` the nav links to.
 */
export function routeKey(pathname: string): string {
  const parts = pathname.toLowerCase().split('/').filter(Boolean);
  // `/casino/goal-game/:gameId` is a launched game, not a lobby category.
  if (parts[0] === 'casino' && parts[1] !== 'goal-game') {
    return `casino:${parts[2] ?? ''}`;
  }
  return `/${parts.join('/')}`;
}

/** This entry's own toggle — the operator's answer for this one row. */
function isEntryOn(link: MenuLink, menus: BrandMenus): boolean {
  return link.group === 'topNav'
    ? menus.topNav[link.id] !== false
    : menus.sidebar[link.id] !== false;
}

/**
 * Whether the router should refuse this URL for the current brand.
 *
 * ANY hidden entry closes the page. Switching a toggle off has to mean the thing
 * is gone — button, URL and the data behind it — or the panel is lying: an
 * operator who turned "Casino" off and then found `/casino/all` still serving
 * the full lobby has no way to tell which of their other switches are equally
 * decorative. The earlier rule (die only once EVERY link is off) made the same
 * click behave differently per entry — `/inplay` really closed because nothing
 * else pointed at it, while `/casino/all` stayed open because the sidebar's "Our
 * Casino" row happened to share the URL — and that difference is invisible from
 * the panel.
 *
 * The link a page loses this way is not left dangling: `isMenuLinkPainted` below
 * is what the nav and the rail filter on, so an entry whose URL is closed stops
 * being drawn even while its own toggle reads on. The two rules together are
 * what make a co-owned page behave like one thing.
 *
 * A URL no menu entry owns is left alone: the panel gives no way to turn it back
 * on, so hiding it would be a change nobody could undo.
 */
export function isRouteHidden(pathname: string, menus: BrandMenus): boolean {
  const key = routeKey(pathname);
  const links = MENU_LINKS.filter((link) => routeKey(link.path) === key);
  if (!links.length) return false;
  return links.some((link) => !isEntryOn(link, menus));
}

/**
 * Whether a nav strip / sidebar entry should be drawn at all.
 *
 * Its own toggle is only half the test. Because a hidden entry now closes the
 * whole page (above), every OTHER entry pointing at that URL has to stop being
 * painted too — otherwise turning off the nav's "Casino" would leave the rail's
 * "Our Casino" row sitting there as a button that 404s. Callers pass the path
 * they were going to link to; an entry with no URL of its own (the rail's sport
 * rows, the home page's chips) is judged on its toggle alone.
 */
export function isMenuLinkPainted(
  path: string | undefined,
  ownToggleOn: boolean,
  menus: BrandMenus,
): boolean {
  if (!ownToggleOn) return false;
  return path === undefined || !isRouteHidden(path, menus);
}

/** Reactive form of {@link isRouteHidden}, re-run when GET /brand lands. */
export function useIsRouteHidden(pathname: string): boolean {
  const menus = useBrandMenus();
  return useMemo(() => isRouteHidden(pathname, menus), [pathname, menus]);
}

/**
 * Whether this brand still has a casino at all.
 *
 * Read off the same link registry rather than a second hand-written list of
 * casino ids, so a nav entry added above is counted here the day it is added.
 * The home page uses it to decide whether its game strip has anywhere to belong:
 * an operator who has switched off every casino lobby did not ask to keep a row
 * of slot thumbnails on the front page.
 */
export function hasVisibleCasino(menus: BrandMenus): boolean {
  return MENU_LINKS.some(
    (link) => routeKey(link.path).startsWith('casino:') && !isRouteHidden(link.path, menus),
  );
}

/** Reactive form of {@link hasVisibleCasino}, with the brand's grant on top:
 *  a revoked Casino leaves nowhere for the strip to lead whatever the menus say,
 *  and `FeatureGate` 404s every lobby it would have linked to. */
export function useHasVisibleCasino(): boolean {
  const menus = useBrandMenus();
  const { casino } = useBrandFeatures();
  return useMemo(
    () => casino !== false && hasVisibleCasino(menus),
    [menus, casino],
  );
}
